import { logWarning } from '@core/logging'

import type {
  IShopifyCartItem,
  IShopifyCartResponse,
  IShopifyProduct,
} from './types'

const SHOPIFY_CART_JS = '/cart.js'

/**
 * Cart mutation URLs have to include `qbk-ignore` so Qikify BoosterKit Shopify extension
 * does not treat Firework cart writes as theme cart changes and reload the page
 * (which closes the player). See CS-15828.
 */
const SHOPIFY_CART_UPDATE_JS = '/cart/update.js?qbk-ignore'

/**
 * Makes a get request to Shopify API at the given url
 */
export const shopifyGET = (url: string): Promise<any> =>
  fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  }).then((resp) => {
    if (!resp.ok) {
      throw new Error(`Getting ${url} failed with status ${resp.status}`)
    }
    return resp.json()
  })

const shopifyPOST = (url: string, body: any): Promise<any> =>
  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  }).then((resp) => {
    if (!resp.ok) {
      throw new Error(`Posting ${url} failed with status ${resp.status}`)
    }
    resp.json()
  })

// Get product information by its search engine friendly handle - returns Promise
/**
 *
 */
export const getProductByHandle = (handle: string): Promise<IShopifyProduct> =>
  shopifyGET(`/products/${handle}.js`)

// Get current cart
/**
 *
 */
export const getCart = (): Promise<IShopifyCartResponse> =>
  shopifyGET(SHOPIFY_CART_JS)

// Updates single item in current cart. Can be used for adding as well.
/**
 *
 */
export const updateItemInCart = (
  id: IShopifyCartItem['id'],
  quantity: number,
): Promise<unknown> =>
  shopifyPOST(SHOPIFY_CART_UPDATE_JS, {
    updates: {
      [id]: quantity,
    },
  })

const SHOPIFY_IMAGE_SIZE = 720

/**
 * Constrains a Shopify product image to `SHOPIFY_IMAGE_SIZE` using the `width` and
 * `height` query parameters documented for the Shopify CDN.
 *
 * Deliberately does not touch the filename. The legacy `_720x720` suffix only works
 * for a hardcoded list of extensions, so each new format Shopify serves silently
 * stopped being resized and reported an error instead (`.heic` in #3485, `.avif`
 * after that). Query parameters are format-agnostic, so there is no list to keep up
 * to date, and a non-Shopify catalog url ignores them rather than 404ing on a
 * filename its host does not recognise.
 *
 * Verified against the Shopify CDN: `?width=720&height=720` returns pixel-identical
 * output to `_720x720`, fitting the image inside the box and keeping aspect ratio.
 */
export const resizeShopifyImage = (url: string): string => {
  const withProtocol = url.startsWith('//') ? `https:${url}` : url
  const parsed = new URL(withProtocol)

  parsed.searchParams.set('width', String(SHOPIFY_IMAGE_SIZE))
  parsed.searchParams.set('height', String(SHOPIFY_IMAGE_SIZE))

  return parsed.toString()
}

/**
 * Log a warning when an error is caught during hydration
 */
export function logHydrationErrorWarning(...args) {
  logWarning('Shopify Hydration Error:', ...args)
}
